import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Card, CardContent } from "@mui/material";
import { useTheme, darken, lighten, alpha } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { getStatsDashboardAction } from "../redux/actions/stats";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import dayjs from "dayjs";
import PeopleIcon from "@mui/icons-material/People";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CakeIcon from "@mui/icons-material/Cake";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import strings from "../i18n/language";

let ageRanges = [
  { name: strings.LABEL_UNKNOWN, min: null, max: null, value: 0 },
  { name: "<18", min: 0, max: 18, value: 0 },
  { name: "18-24", min: 18, max: 24, value: 0 },
  { name: "25-34", min: 25, max: 34, value: 0 },
  { name: "35-44", min: 35, max: 44, value: 0 },
  { name: "45-54", min: 45, max: 54, value: 0 },
  { name: "55-64", min: 55, max: 64, value: 0 },
  { name: ">64", min: 65, max: Infinity, value: 0 },
];

const data = [
  { name: "18", value: 10 },
  { name: "18-34", value: 30 },
  { name: "35-44", value: 20 },
  { name: "45-64", value: 20 },
  { name: ">64", value: 20 },
];

const Dashboard = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const stats = useSelector((state) => state.stats.stats);
  const primaryColor = theme.palette.primary.main;
  const [genderDistribution, setGenderDistribution] = useState([]);
  const [ageDistribution, setAgeDistribution] = useState([]);
  const [newPatientsEvolution, setNewPatientsEvolution] = useState([]);
  const [paymentsEvolution, setPaymentsEvolution] = useState({ USD: [], BOL: [] });

  const COLORS = [
    primaryColor,
    lighten(primaryColor, 0.2),
    darken(primaryColor, 0.2),
    alpha(primaryColor, 0.7),
    darken(primaryColor, 0.4),
  ];

  useEffect(() => {
    if (stats) {
      let totalGender =
        parseInt(stats.genderDistribution.man) +
        parseInt(stats.genderDistribution.unknown) +
        parseInt(stats.genderDistribution.woman);
      if (totalGender)
        setGenderDistribution([
          {
            name:
              strings.LABEL_MAN +
              " (" +
              parseInt((stats.genderDistribution.man * 100) / totalGender) +
              "%)",
            value: stats.genderDistribution.man,
          },
          {
            name:
              strings.LABEL_UNKNOWN +
              " (" +
              parseInt((stats.genderDistribution.unknown * 100) / totalGender) +
              "%)",
            value: stats.genderDistribution.unknown,
          },
          {
            name:
              strings.LABEL_WOMAN +
              " (" +
              parseInt((stats.genderDistribution.woman * 100) / totalGender) +
              "%)",
            value: stats.genderDistribution.woman,
          },
        ]);
      //age distribution
      let totalAges = 0;
      Object.entries(ageRanges).map(([key, value]) => {
        if (ageRanges[key].value) return null;
        if (value.name === strings.LABEL_UNKNOWN) {
          ageRanges[key].value = stats.ageDistribution.unknown;
          totalAges += stats.ageDistribution.unknown;
        } else {
          Object.entries(stats.ageDistribution).map(([age, count]) => {
            if (value.min <= age && age <= value.max) {
              ageRanges[key].value += count;
              totalAges += count;
            }
            return null;
          });
        }
        return null;
      });
      let resultAges = ageRanges.filter((item) => item.value);
      setAgeDistribution([]);
      Object.keys(resultAges).forEach(function (key, index) {
        if (!resultAges[key].name.includes("%")) {
          resultAges[index].name = `${resultAges[index].name} ${parseInt(
            (resultAges[index].value * 100) / totalAges
          )}%`;
        }
        return null;
      });
      setAgeDistribution(resultAges);

      let resultNewPatients = [];
      stats.newPatientsByMonth.map(({ month, number_of_patients }) => {
        resultNewPatients.push({
          name: dayjs(month).format("MMM YY"),
          total: number_of_patients,
        });
        return null;
      });
      setNewPatientsEvolution(resultNewPatients);

      // payments by month - pivot by currency
      const paymentsUSD = [];
      const paymentsBOL = [];
      const paymentsMap = {};
      (stats.paymentsByMonth || []).forEach(({ month, currency, total }) => {
        if (!paymentsMap[month]) paymentsMap[month] = { name: dayjs(month).format("MMM YY"), USD: 0, BOL: 0 };
        paymentsMap[month][currency] = parseFloat(total) || 0;
      });
      Object.values(paymentsMap).forEach((entry) => {
        paymentsUSD.push({ name: entry.name, total: entry.USD });
        paymentsBOL.push({ name: entry.name, total: entry.BOL });
      });
      setPaymentsEvolution({ USD: paymentsUSD, BOL: paymentsBOL });
    } else dispatch(getStatsDashboardAction());
  }, [stats, dispatch]);

  if (!stats) return null;
  else
    return (
      <Box sx={{ flexGrow: 1, mx: 2 }}>
        <Grid container spacing={3}>
          {/* Pacientes Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <PeopleIcon sx={{ opacity: 0.7 }} />
                  <Typography variant="h6">{strings.LABEL_PATIENTS}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                  <Typography variant="body1">{strings.LABEL_TOTAL}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.numberOfPatients}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                  <Typography variant="body1">{strings.LABEL_ACTIVE_SUBSCRIPTION}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.numberActivePatient || 0}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                  <Typography variant="body1">{strings.LABEL_NO_SUBSCRIPTION}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.numberNoSubscriptionPatient || 0}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <Typography variant="body1">{strings.LABEL_ARCHIVED}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.numberOfArchivedPatients || 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Suscripciones Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <CardMembershipIcon sx={{ opacity: 0.7 }} />
                  <Typography variant="h6">{strings.LABEL_SUBSCRIPTIONS}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                  <Typography variant="body1">{strings.LABEL_TOTAL}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.subscriptionsAll}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                  <Typography variant="body1">{strings.LABEL_ACTIVE_F}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.subscriptionsActive || 0}</Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                  <Typography variant="body1">{strings.LABEL_ARCHIVED_F}</Typography>
                  <Typography variant="h6" fontWeight="bold">{stats.subscriptionsArchived || 0}</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Pagos del mes Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                  <AttachMoneyIcon sx={{ opacity: 0.7 }} />
                  <Typography variant="h6">{strings.LABEL_PAYMENTS_THIS_MONTH}</Typography>
                </Box>
                {(() => {
                  const totals = stats.paymentTotals || [];
                  const usd = totals.find((t) => t.currency === "USD");
                  const bol = totals.find((t) => t.currency === "BOL");
                  const count = totals.reduce((sum, t) => sum + Number(t.count || 0), 0);
                  return (
                    <>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                        <Typography variant="body1">{strings.LABEL_TOTAL_PAYMENTS}</Typography>
                        <Typography variant="h6" fontWeight="bold">{count}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.5 }}>
                        <Typography variant="body1">{strings.LABEL_TOTAL_USD}</Typography>
                        <Typography variant="h6" fontWeight="bold">${parseFloat(usd?.totalAmount || 0).toFixed(1)}</Typography>
                      </Box>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
                        <Typography variant="body1">{strings.LABEL_TOTAL_BOL}</Typography>
                        <Typography variant="h6" fontWeight="bold">Bs.{parseFloat(bol?.totalAmount || 0).toFixed(1)}</Typography>
                      </Box>
                    </>
                  );
                })()}
              </CardContent>
            </Card>
          </Grid>
          {/* Evolution of patients */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  {strings.MESSAGE_NEW_PATIENTS_BY_MONTH}
                </Typography>
                <ResponsiveContainer width="100%" minHeight={300}>
                  <AreaChart
                    width={500}
                    height={400}
                    data={newPatientsEvolution}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Payments evolution USD */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  {strings.MESSAGE_PAYMENTS_BY_MONTH} (USD)
                </Typography>
                <ResponsiveContainer width="100%" minHeight={300}>
                  <AreaChart data={paymentsEvolution.USD}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={theme.palette.success.main}
                      fill={theme.palette.success.main}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          {/* Payments evolution BOL */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  {strings.MESSAGE_PAYMENTS_BY_MONTH} (BOL)
                </Typography>
                <ResponsiveContainer width="100%" minHeight={300}>
                  <AreaChart data={paymentsEvolution.BOL}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="total"
                      stroke={theme.palette.primary.main}
                      fill={theme.palette.primary.main}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>

          {/* Age Distribution Patients */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  {strings.MESSAGE_DISTRIBUTION_PATIENTS_BY_AGE}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart width={400} height={400}>
                    <Pie
                      data={ageDistribution}
                      labelLine={false}
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name }) => name}
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          {/* Gender Distribution Patients */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  {strings.MESSAGE_DISTRIBUTION_PATIENTS_BY_GENDER}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <PieChart width={400} height={400}>
                    <Pie
                      data={genderDistribution}
                      labelLine={false}
                      outerRadius={110}
                      fill="#8884d8"
                      dataKey="value"
                      label={({ name }) => name}
                    >
                      {data.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
          {/* Today birthdays */}
          {stats.birthdayThisMonth?.filter(
            (p) =>
              dayjs(p.birthDate).date() === dayjs().date() &&
              dayjs(p.birthDate).month() === dayjs().month()
          ).length > 0 && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Box
                    mt={2}
                    style={{
                      flexDirection: "row",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <CakeIcon style={{ marginRight: 10 }} />
                    <Typography variant="h5" component="div">
                      {strings.MESSAGE_BIRTHDAYS_TODAY}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                      ({stats.birthdayThisMonth.filter(
                        (p) =>
                          dayjs(p.birthDate).date() === dayjs().date() &&
                          dayjs(p.birthDate).month() === dayjs().month()
                      ).length})
                    </Typography>
                  </Box>
                  <Box mt={2}>
                    {stats.birthdayThisMonth
                      .filter(
                        (p) =>
                          dayjs(p.birthDate).date() === dayjs().date() &&
                          dayjs(p.birthDate).month() === dayjs().month()
                      )
                      .map((patient, index) => (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="flex-start"
                          flexDirection="column"
                          mb={1}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            {patient.displayName}
                          </Typography>
                          {patient.phone && (
                            <div
                              style={{
                                flexDirection: "row",
                                display: "flex",
                                alignItems: "center",
                                marginTop: 5,
                              }}
                            >
                              <LocalPhoneIcon style={{ marginRight: 5, fontSize: 20 }} />
                              <Typography variant="body2">{patient.phone}</Typography>
                            </div>
                          )}
                          {patient.mail && (
                            <div
                              style={{
                                flexDirection: "row",
                                display: "flex",
                                alignItems: "center",
                                marginTop: 5,
                              }}
                            >
                              <AlternateEmailIcon style={{ marginRight: 5, fontSize: 20 }} />
                              <Typography variant="body2">{patient.mail}</Typography>
                            </div>
                          )}
                        </Box>
                      ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
          {/* Month birthdays */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box
                  mt={2}
                  style={{
                    flexDirection: "row",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <CakeIcon style={{ marginRight: 10 }}></CakeIcon>
                  <Typography variant="h5" component="div">
                    {strings.MESSAGE_BIRTHDAYS_PATIENTS}
                  </Typography>
                  {stats.birthdayThisMonth?.length > 0 && (
                    <Typography variant="body2" color="textSecondary" style={{ marginLeft: 8 }}>
                      ({stats.birthdayThisMonth.length})
                    </Typography>
                  )}
                </Box>

                <Box mt={2} maxHeight={320} overflow="auto">
                  {stats.birthdayThisMonth &&
                  stats.birthdayThisMonth.length > 0 ? (
                    stats.birthdayThisMonth.map((patient, index) => {
                      const isToday =
                        dayjs(patient.birthDate).date() === dayjs().date() &&
                        dayjs(patient.birthDate).month() === dayjs().month();
                      return (
                        <Box
                          key={index}
                          display="flex"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          flexDirection="column"
                          color={isToday ? "red" : "inherit"}
                          mb={1}
                        >
                          <Typography variant="body1" fontWeight="bold">
                            {`${dayjs(patient.birthDate).format("LL")} - ${
                              patient.displayName
                            }`}
                          </Typography>
                          {patient.phone && (
                            <div
                              style={{
                                flexDirection: "row",
                                display: "flex",
                                alignItems: "center",
                                marginTop: 5,
                              }}
                            >
                              <LocalPhoneIcon
                                style={{ marginRight: 5, fontSize: 20 }}
                              ></LocalPhoneIcon>
                              <Typography variant="body2">
                                {patient.phone}
                              </Typography>
                            </div>
                          )}
                          {patient.mail && (
                            <div
                              style={{
                                flexDirection: "row",
                                display: "flex",
                                alignItems: "center",
                                marginTop: 5,
                              }}
                            >
                              <AlternateEmailIcon
                                style={{ marginRight: 5, fontSize: 20 }}
                              ></AlternateEmailIcon>
                              <Typography variant="body2">
                                {patient.mail}
                              </Typography>
                            </div>
                          )}
                        </Box>
                      );
                    })
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      {strings.MESSAGE_NO_BIRTHDAYS_THIS_MONTH}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
};

export default Dashboard;
